import { t } from 'i18next'
import { type NavigateFunction } from 'react-router'
import { toast } from 'react-toastify'
import { GlobalHotKeyResult } from '~/atoms/components/GlobalHotKeys'
import { StorageKeys } from '~/atoms/StorageKeys'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import Logger from '~/services/Logger'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { isElectron } from '~/utils/common'
import { findBrowserOnlyRoute } from '~/utils/desktopAppHandoff'
import { deepLinkToInAppPath, isMainShellPath } from '~/utils/desktopDeepLink'
import { buildPopupSettingsPayload } from '~/utils/leanPopup'
import {
  getLocalStorageObjectKey,
  setLocalStorageObjectKey,
} from '~/utils/localStorageClient'

import { IpcRendererListener } from '@mangovoice/types'

const logger = new Logger('ElectronIPC')

/** Error type only: IPC and call errors can echo URLs, SIP URIs or payloads. */
const errorName = (error: unknown): string =>
  error instanceof Error ? error.name : typeof error

export const events = {
  deepLink: 'deep-link',
  electronLog: 'electron-log',
  openSettings: 'open-settings',
  openMessages: 'open-messages',
  globalCopyNumberExperiment: 'global-copy-number-hotkey',
  globalCopyNumberExperimentResult: 'global-copy-number-hotkey-result',
  popupSettings: 'popup-settings',
  setPopupDimensionsInLocalStorage: 'set-popup-dimensions-in-localstorage',
  changePopupDimensions: 'change-popup-dimensions',
  updateMenusAfterAuth: 'update-menus-after-auth',
  setColorMode: 'set-color-mode',
  colorModeChanged: 'color-mode-changed',
  answerCall: 'popup-answer-call',
  answerCallWithHold: 'popup-answer-call-with-hold',
  answerCallWithEnd: 'popup-answer-call-with-end',
  answerCallWithPark: 'popup-answer-call-with-park',
  rejectCall: 'popup-reject-call',
  parkCall: 'popup-park-call',
  holdCall: 'popup-hold-call',
  resumeCall: 'popup-resume-call',
  endCall: 'popup-end-call',
  switchCall: 'popup-switch-call',
  endAllSoftphoneCalls: 'end-all-softphone-calls',
}

const localStorageKeys = {
  experiments: 'experiments',
  popupSettings: 'popupSettings',
  popupDimensions: 'windowDimensions',
}

type Dimensions = {
  height: number
  width: number
}

const defaultDimensions: Dimensions = {
  width: 275,
  height: 200,
}

export type DeepLinkCallbacks = {
  onChangelog?: () => void
}

/** The link the desktop app was launched with, taken from main once per load. */
let launchDeepLink: { url: Promise<string | null>; handled: boolean } | null =
  null

const takeLaunchDeepLink = async (): Promise<string | null> => {
  try {
    const url = await window.electron?.invoke?.('deep-link:take-launch-url')
    return typeof url === 'string' ? url : null
  } catch (error) {
    logger.warn('Failed to fetch launch deep link', {
      errorName: errorName(error),
    })
    return null
  }
}

/** Test-only: forget the cached launch link so each test starts fresh. */
export const resetLaunchDeepLinkForTests = () => {
  launchDeepLink = null
}

const CHANGELOG_DEEP_LINK_PATH = '/changelog'

type IgnoredDeepLinkReason = 'unsafe' | 'outside-shell' | 'unknown'

/**
 * Logs an ignored deep link by reason and known route only, never the URL:
 * paths and queries can carry tokens (secure fax, password reset), and the
 * logger forwards to Sentry and CloudWatch.
 */
const logIgnoredDeepLink = (
  reason: IgnoredDeepLinkReason,
  route?: string | null,
) => {
  logger.warn('Ignored deep link', route ? { reason, route } : { reason })
}

/**
 * Routes an incoming `mango://` or `tel:` URL, whether pushed by main (warm
 * start) or pulled once on mount (cold start). A main-shell path that passes
 * `deepLinkToInAppPath` is navigated to as-is (query and hash included).
 * Anything else (unsafe, unknown, or outside the shell, like `/login` or
 * `/popup`) is ignored so the current page, and any active call, is kept.
 */
export const deepLinkListener = (
  url: string,
  navigate: NavigateFunction,
  callbacks?: DeepLinkCallbacks,
) => {
  if (!url) {
    logger.warn('Received empty deep link')
    return
  }

  if (url.startsWith('tel:')) {
    const phoneNumber = url.replace(/^tel:\/?\/?/, '')
    navigate(`${ApplicationRoutes.WEB_PHONE}?number=${phoneNumber}`)
    return
  }

  const path = deepLinkToInAppPath(url)
  if (!path) {
    logIgnoredDeepLink('unsafe')
    return
  }

  if (path.split(/[?#]/)[0] === CHANGELOG_DEEP_LINK_PATH) {
    callbacks?.onChangelog?.()
    return
  }

  if (!isMainShellPath(path)) {
    const route = findBrowserOnlyRoute(path.split(/[?#]/)[0])
    logIgnoredDeepLink(route ? 'outside-shell' : 'unknown', route)
    return
  }

  navigate(path)
}

/**
 * Listens for an event that triggers navigation to the messaging page.
 *
 * @param {function} navigate - The navigation function used to change the route
 * to the messaging page.
 * @param {object} data - Optional data containing threadId to open a specific thread
 */
const gotoMessagingListener = (
  navigate: NavigateFunction,
  data?: { threadId?: number },
) => {
  if (data?.threadId) {
    navigate(`${ApplicationRoutes.MESSAGING}?threadId=${data.threadId}`)
  } else {
    navigate(ApplicationRoutes.MESSAGING)
  }
}

/**
 * Navigates to the settings page when triggered by a hotkey event.
 *
 * @param navigate - The navigation function used to change the route
 * to the settings page.
 */
const settingsHotkeyListener = (navigate: any) => {
  navigate(ApplicationRoutes.SETTINGS)
}

/**
 * Listens for the event to send the popup window's call pop settings data to the
 * Electron main process.
 *
 * The data is retrieved from local storage using the key
 * `StorageKeys.CallPop` (which is 'callPop'). If the key is not present, an empty object is
 * sent instead.
 */
export const sendPopupSettings = () => {
  try {
    // Read from the correct storage key that matches callPopStateAtom
    const callPopData = window.localStorage.getItem(StorageKeys.CallPop) || '{}'

    // Parse to ensure it's valid JSON and extract callPopEnabled
    let callPopEnabled = true // Default to true if not set
    try {
      const parsed = JSON.parse(callPopData)
      callPopEnabled = parsed?.callPopEnabled ?? true
    } catch {
      // If parsing fails, default to true
      callPopEnabled = true
    }

    // Send in the format expected by the Electron main process
    window.electron?.send(
      events.popupSettings,
      buildPopupSettingsPayload(callPopEnabled),
    )
  } catch (e) {
    logger.warn('Failed to send call pop settings', { errorName: errorName(e) })
    // Send default value on error
    window.electron?.send(events.popupSettings, buildPopupSettingsPayload(true))
    return
  }
}

const globalCopyNumberHotKeyListener = () => {
  try {
    window.electron?.send(
      events.globalCopyNumberExperiment,
      window.localStorage.getItem(localStorageKeys.experiments) || '{}',
    )
  } catch (e) {
    logger.warn('Failed to send global copy number experiment', {
      errorName: errorName(e),
    })
    return
  }
}

const globalCopyNumberHotKeyResponseListener = (type: 'success' | 'error') => {
  try {
    if (type === 'success') {
      toast.success(t('global.toast.globalPhoneHotKeySuccess'))
    } else {
      toast.error(t('global.toast.globalPhoneHotKeyError'))
    }
  } catch (e) {
    logger.warn('Failed to show global copy number toast', {
      errorName: errorName(e),
    })
    return
  }
}

/**
 * Listens for the event to get the popup window's dimensions and sends the
 * dimensions to the Electron main process.
 */
const popupGetDimensionsListener = () => {
  try {
    const dimensions = getLocalStorageObjectKey({
      storageKey: localStorageKeys.popupSettings,
      keyInObject: localStorageKeys.popupDimensions,
      initialValue: defaultDimensions,
    })

    window.electron?.send(events.changePopupDimensions, dimensions)
  } catch (e) {
    logger.warn('Failed to send popup dimensions', { errorName: errorName(e) })
    return
  }
}

/**
 * Updates the local storage with the dimensions of the popup window.
 *
 * @param {number} args.height - The height of the popup window.
 * @param {number} args.width - The width of the popup window.
 */
const popupSetDimensionsListenerInLocalStorage = (args: {
  height: number
  width: number
}) => {
  setLocalStorageObjectKey<Dimensions>(
    {
      storageKey: localStorageKeys.popupSettings,
      keyInObject: localStorageKeys.popupDimensions,
      initialValue: defaultDimensions,
    },
    args,
  )
}

/**
 * Sends a message to the Electron main process to update the application's menus.
 *
 * @param {boolean} args.enableAuthedMenuItems - If true, includes menu items that are only enabled when the user is authenticated.
 */
const updateMenus = (args: { enableAuthedMenuItems?: boolean } = {}) => {
  const { enableAuthedMenuItems = false } = args

  try {
    window.electron?.send(events.updateMenusAfterAuth, {
      enableAuthedMenuItems,
    })
  } catch (e) {
    logger.warn('Failed to update menus', { errorName: errorName(e) })
    return
  }
}

const handleAnswerPhoneCall = (args: {
  answerCallFn: (callUuid?: string) => void
  callUuid?: string
}) => {
  try {
    args.answerCallFn(args.callUuid)
  } catch (error) {
    logger.warn('Error answering phone call', { errorName: errorName(error) })
  }
}

const handleRejectPhoneCall = (args: {
  rejectCallFn: (callUuid?: string) => void
  callUuid?: string
}) => {
  try {
    args.rejectCallFn(args.callUuid)
  } catch (error) {
    logger.warn('Error rejecting phone call', { errorName: errorName(error) })
  }
}

const handleAnswerCallWithEnd = (args: {
  answerCallWithEndFn: (callUuid?: string) => void
  callUuid?: string
}) => {
  try {
    args.answerCallWithEndFn(args.callUuid)
  } catch (error) {
    logger.warn('Error answering call with end', {
      errorName: errorName(error),
    })
  }
}

const handleAnswerCallWithHold = (args: {
  answerCallHoldFn: (callUuid?: string) => void
  callUuid?: string
}) => {
  try {
    args.answerCallHoldFn(args.callUuid)
  } catch (error) {
    logger.warn('Error answering call with hold', {
      errorName: errorName(error),
    })
  }
}

const handleParkCall = (args: {
  answerCallWithParkCurrentFn: (parkTarget: string, callUuid?: string) => void
  parkTarget: string
  callUuid?: string
}) => {
  try {
    args.answerCallWithParkCurrentFn(args.parkTarget, args.callUuid)
  } catch (error) {
    logger.warn('Error parking call', { errorName: errorName(error) })
  }
}

const handleHoldCall = (
  holdCallFn: (callUuid: string) => Promise<void>,
  callUuid: string,
) => {
  try {
    void holdCallFn(callUuid)
  } catch (error) {
    logger.warn('Error holding call', { errorName: errorName(error) })
  }
}

const handleResumeCall = (
  resumeCallFn: (callUuid: string) => Promise<void>,
  callUuid: string,
) => {
  try {
    return resumeCallFn(callUuid)
  } catch (error) {
    logger.warn('Error resuming call', { errorName: errorName(error) })
  }
}

const handleEndCall = (
  endCallFn: (callUuid: string) => void,
  callUuid: string,
) => {
  try {
    endCallFn(callUuid)
  } catch (error) {
    logger.warn('Error ending call', { errorName: errorName(error) })
  }
}

const handleSwitchCall = (
  switchCallFn: (callUuid: string) => Promise<void>,
  callUuid: string,
) => {
  try {
    return switchCallFn(callUuid)
  } catch (error) {
    logger.warn('Error switching call', { errorName: errorName(error) })
  }
}

type HandlePhoneCallProps = {
  answerCall: (callUuid?: string) => void
  rejectCall: (callUuid?: string) => void
  answerCallWithEnd: (callUuid?: string) => void
  answerCallWithHold: (callUuid?: string) => void
  answerCallWithParkCurrent: (parkTarget: string, callUuid?: string) => void
  holdCall: (callUuid: string) => Promise<void>
  resumeCall: (callUuid: string) => Promise<void>
  endCall: (callUuid: string) => void
  endAllSoftphoneCalls: () => void
  switchCall: (callUuid: string) => Promise<void>
  parkingLots: TrackedParkingLot[]
}

/**
 * Provides methods to interact with the Electron main process.
 *
 * This hook listens to the following events:
 *
 * - `deepLink`: Listens to the event to handle deep links.
 * - `openSettings`: Listens to the event to open the settings window.
 * - `popupSettings`: Listens to the event to retrieve the popup window's
 *   call pop settings.
 * - `openMessages`: Listens to the event to open the messaging window.
 * - `getPopupDimensions`: Listens to the event to retrieve the popup window's
 *   dimensions.
 * - `setPopupDimensions`: Listens to the event to update the popup window's
 *   dimensions.
 *
 * The hook also provides methods to remove the listeners and to recreate the
 * menu bar.
 *
 * @returns An object with methods to interact with the Electron main process.
 */
const useElectronIPC = () => {
  // Prevent rapid re-registration of listeners
  const registrationTimeouts = new Map<string, NodeJS.Timeout>()

  const removeListener = (
    channelName: string,
    channelListener: IpcRendererListener,
  ) => {
    if (
      window.electron &&
      typeof window.electron.removeListener === 'function'
    ) {
      window.electron.removeListener(channelName, channelListener)
    }
  }

  const initDeepLinkListener = (
    navigate: NavigateFunction,
    callbacks?: DeepLinkCallbacks,
  ) => {
    const deepLinkFn = (url: string) =>
      deepLinkListener(url, navigate, callbacks)

    window.electron?.receive(events.deepLink, deepLinkFn)

    // Cold start: main holds the link the app was launched with until we ask
    // for it, since anything it pushed before this listener existed was lost.
    // One take per page load, shared so a StrictMode remount still gets it.
    launchDeepLink ??= { url: takeLaunchDeepLink(), handled: false }
    const pending = launchDeepLink
    let disposed = false
    void pending.url.then((url) => {
      if (!url || disposed || pending.handled) return
      pending.handled = true
      deepLinkFn(url)
    })

    return () => {
      disposed = true
      removeListener(events.deepLink, deepLinkFn)
    }
  }

  const initSettingsHotkeyListener = (navigate: NavigateFunction) => {
    const settingsHotKeyFn = () => settingsHotkeyListener(navigate)

    window.electron?.receive(events.openSettings, settingsHotKeyFn)
    return () => removeListener(events.openSettings, settingsHotKeyFn)
  }

  const initGetCallPopSettingsListener = () => {
    window.electron?.receive(events.popupSettings, sendPopupSettings)
    // Also send current call pop settings after a delay to ensure React state and localStorage are ready
    // Use a longer delay to ensure the component tree is fully mounted and atoms are initialized
    setTimeout(sendPopupSettings, 300)
    return () => removeListener(events.popupSettings, sendPopupSettings)
  }

  const initGetCopyNumberHotKeyResultListener = (
    callback?: (type: GlobalHotKeyResult) => void,
    fireDefaultCallback: boolean = true,
  ) => {
    const listener = () => globalCopyNumberHotKeyListener()
    const responseListener = (type: GlobalHotKeyResult) => {
      if (callback) {
        callback?.(type)
        if (fireDefaultCallback) {
          globalCopyNumberHotKeyResponseListener(type)
        }
      } else {
        globalCopyNumberHotKeyResponseListener(type)
      }
    }

    window.electron?.receive(events.globalCopyNumberExperiment, listener)

    window.electron?.receive(
      events.globalCopyNumberExperimentResult,
      responseListener,
    )

    return () => {
      removeListener(events.globalCopyNumberExperiment, listener)
      removeListener(events.globalCopyNumberExperimentResult, responseListener)
    }
  }

  const initGoToMessagingListener = (navigate: NavigateFunction) => {
    const gotoMessagingFn = (data?: { threadId?: number }) =>
      gotoMessagingListener(navigate, data)

    window.electron?.receive(events.openMessages, gotoMessagingFn)
    return () => removeListener(events.openMessages, gotoMessagingFn)
  }

  const initGetPopupWindowDimensionsListener = () => {
    window.electron?.receive(
      events.changePopupDimensions,
      popupGetDimensionsListener,
    )

    return () =>
      removeListener(events.changePopupDimensions, popupGetDimensionsListener)
  }

  const initSetPopupWindowDimensionsInStorageListener = () => {
    window.electron?.receive(
      events.setPopupDimensionsInLocalStorage,
      popupSetDimensionsListenerInLocalStorage,
    )

    return () =>
      removeListener(
        events.setPopupDimensionsInLocalStorage,
        popupSetDimensionsListenerInLocalStorage,
      )
  }

  const initHandlePhoneCallListener = ({
    answerCall,
    rejectCall,
    answerCallWithEnd,
    answerCallWithHold,
    answerCallWithParkCurrent,
    holdCall,
    resumeCall,
    endCall,
    endAllSoftphoneCalls,
    switchCall,
  }: HandlePhoneCallProps) => {
    if (!isElectron) {
      return () => {}
    }

    // Clear any existing timeout for this registration
    const existingTimeout = registrationTimeouts.get('phoneCallListener')
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    // Debounce registration to prevent infinite loops
    const timeoutId = setTimeout(() => {
      registrationTimeouts.delete('phoneCallListener')
    }, 1000)
    registrationTimeouts.set('phoneCallListener', timeoutId)

    const handleAnswer = (callUuid?: string) =>
      handleAnswerPhoneCall({ answerCallFn: answerCall, callUuid })

    const handleReject = (callUuid?: string) =>
      handleRejectPhoneCall({ rejectCallFn: rejectCall, callUuid })

    const handleAnswerEnd = (callUuid?: string) =>
      handleAnswerCallWithEnd({
        answerCallWithEndFn: answerCallWithEnd,
        callUuid,
      })

    const handleAnswerHold = (callUuid?: string) =>
      handleAnswerCallWithHold({
        answerCallHoldFn: answerCallWithHold,
        callUuid,
      })

    const handlePark = (parkTarget?: string, callUuid?: string) =>
      handleParkCall({
        answerCallWithParkCurrentFn: answerCallWithParkCurrent,
        parkTarget: parkTarget || '',
        callUuid,
      })

    const handleAnswerWithPark = (parkTarget?: string, callUuid?: string) => {
      return handleParkCall({
        answerCallWithParkCurrentFn: answerCallWithParkCurrent,
        parkTarget: parkTarget || '',
        callUuid,
      })
    }

    const handleHold = (callUuid: string) => {
      handleHoldCall(holdCall, callUuid)
    }

    const handleResume = (callUuid: string) => {
      void handleResumeCall(resumeCall, callUuid)
    }

    const handleEnd = (callUuid: string) => {
      handleEndCall(endCall, callUuid)
    }

    const handleSwitch = (callUuid: string) => {
      try {
        void handleSwitchCall(switchCall, callUuid)
      } catch (error) {
        logger.warn('Error in handleSwitch', { errorName: errorName(error) })
      }
    }

    const handleEndAllSoftphoneCalls = () => {
      try {
        endAllSoftphoneCalls()
      } catch (error) {
        logger.warn('Error ending all softphone calls', {
          errorName: errorName(error),
        })
      }
    }

    window.electron?.receive(events.answerCall, handleAnswer)
    window.electron?.receive(events.rejectCall, handleReject)
    window.electron?.receive(events.answerCallWithEnd, handleAnswerEnd)
    window.electron?.receive(events.answerCallWithHold, handleAnswerHold)
    window.electron?.receive(events.answerCallWithPark, handleAnswerWithPark)
    window.electron?.receive(events.parkCall, handlePark)
    window.electron?.receive(events.holdCall, handleHold)
    window.electron?.receive(events.resumeCall, handleResume)
    window.electron?.receive(events.endCall, handleEnd)
    window.electron?.receive(events.switchCall, handleSwitch)
    window.electron?.receive(
      events.endAllSoftphoneCalls,
      handleEndAllSoftphoneCalls,
    )

    return () => {
      removeListener(events.answerCall, handleAnswer)
      removeListener(events.rejectCall, handleReject)
      removeListener(events.answerCallWithEnd, handleAnswerEnd)
      removeListener(events.answerCallWithHold, handleAnswerHold)
      removeListener(events.answerCallWithPark, handleAnswerWithPark)
      removeListener(events.parkCall, handlePark)
      removeListener(events.holdCall, handleHold)
      removeListener(events.resumeCall, handleResume)
      removeListener(events.endCall, handleEnd)
      removeListener(events.switchCall, handleSwitch)
      removeListener(events.endAllSoftphoneCalls, handleEndAllSoftphoneCalls)
    }
  }

  return {
    initDeepLinkListener,
    initSettingsHotkeyListener,
    initGetCallPopSettingsListener,
    initGetCopyNumberHotKeyResultListener,
    initGoToMessagingListener,
    initGetPopupWindowDimensionsListener,
    initSetPopupWindowDimensionsInStorageListener,
    updateMenus,
    initHandlePhoneCallListener: ({
      answerCall,
      rejectCall,
      answerCallWithEnd,
      answerCallWithHold,
      answerCallWithParkCurrent,
      holdCall,
      resumeCall,
      endCall,
      endAllSoftphoneCalls,
      switchCall,
      parkingLots,
    }: HandlePhoneCallProps) =>
      initHandlePhoneCallListener({
        answerCall,
        rejectCall,
        answerCallWithEnd,
        answerCallWithHold,
        answerCallWithParkCurrent,
        holdCall,
        resumeCall,
        endCall,
        endAllSoftphoneCalls,
        switchCall,
        parkingLots,
      }),
  }
}

/**
 * Changelog IPC wrappers
 */

export interface ChangelogData {
  version: string
  name: string
  body: string
  published_at: string
}

export interface ChangelogHistory {
  releases: ChangelogData[]
}

/**
 * Fetch changelog history (last 10 releases) via IPC
 */
export const fetchChangelogHistory =
  async (): Promise<ChangelogHistory | null> => {
    if (!isElectron || !window.electron?.invoke) {
      return null
    }

    try {
      return await window.electron.invoke('changelog:fetch-history')
    } catch (error) {
      logger.warn('Failed to fetch changelog history', {
        errorName: errorName(error),
      })
      return null
    }
  }

/**
 * Fetch changelog for current app version via IPC
 */
export const fetchChangelogForViewing =
  async (): Promise<ChangelogData | null> => {
    if (!isElectron || !window.electron?.invoke) {
      return null
    }

    try {
      return await window.electron.invoke('changelog:fetch-for-viewing')
    } catch (error) {
      logger.warn('Failed to fetch changelog for viewing', {
        errorName: errorName(error),
      })
      return null
    }
  }

export default useElectronIPC
